// Reduções da jornada, separadas da tela para poderem ser testadas sem montar
// componente.
import type { PermissionCatalog } from "./api.js";
import type { Journey, JourneyStepStatus } from "@/api/onboarding";
import { JOURNEY_CATALOG, type JourneyStepDef } from "./journey-catalog.js";
import { canAccess } from "./lib/access.js";

// Argumentos de acesso compartilhados pelas três funções abaixo — os mesmos
// que canAccess consome (catálogo + permissões + acesso cross-marca).
export type JourneyAccess = {
  catalog: PermissionCatalog | null;
  permissions: ReadonlySet<string>;
  crossBrand?: boolean;
};

export type JourneyItem = JourneyStepDef & { status: JourneyStepStatus };

// Ordem e conteúdo vêm do catálogo; o estado vem do back. Passo que o back
// devolve e o front não conhece SOME da lista — a tela continua de pé, e o
// contador ("3 de 7", que vem do back) denuncia a divergência em vez de
// escondê-la.
export function mergeJourney(resp: Journey): JourneyItem[] {
  const status = new Map(resp.steps.map((s) => [s.id, s.status]));
  return JOURNEY_CATALOG[resp.journey]
    .filter((def) => status.has(def.id))
    .map((def) => ({ ...def, status: status.get(def.id) as JourneyStepStatus }));
}

// Passo `todo` cujo destino a guarda de rota barra para este papel: não há CTA
// que funcione ali (o clique só voltaria pela guarda), então não é pendência
// ACIONÁVEL — mesmo critério de `blocked`, só que decidido no front em vez de
// vir pronto do back (ex.: gerente e o passo "Convide o time", que só diretor
// alcança em /management/users). A tela de jornada continua MOSTRANDO o passo;
// ele só sai da conta de pendência e dos próximos passos.
export function semAcesso(item: JourneyItem, access: JourneyAccess): boolean {
  return item.status === "todo" && !!item.to && !canAccess({ ...access, path: item.to });
}

// Só `todo` DENTRO do alcance da permissão é pendência. `blocked` depende de
// outra pessoa (ex.: telefonia); `todo` fora do alcance depende de OUTRO PAPEL
// (ex.: diretor) — os dois manteriam o destaque preso para sempre se contados.
export function temPendencia(items: JourneyItem[], access: JourneyAccess): boolean {
  return items.some((i) => i.status === "todo" && !semAcesso(i, access));
}

export function proximosPassos(items: JourneyItem[], access: JourneyAccess, n = 2): JourneyItem[] {
  return items.filter((i) => i.status === "todo" && !semAcesso(i, access)).slice(0, n);
}
